import React, { useEffect, useRef, useState } from "react";
import { Mantra, PhoneticSegment } from "../types";
import { Mic, MicOff, RotateCcw, Volume2, Sparkles, Check, Info, Play, Pause, Sliders, Activity, Radio } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { createAudioContext } from "../lib/audio-utils";
import { detectVedicSwaraPitch, calculateSpectralCentroid, analyzeFrame, SpectralCentroidResult, PitchDetectionResult } from "@sovereign/vedic-phonetics";
import { pitchWorkerManager } from "../lib/pitch-worker-manager";

interface PitchVisualizerProps {
  activeMantra: Mantra;
  onSessionComplete?: (score: number, matches: number, duration: number) => void;
  language?: string;
}

export default function PitchVisualizer({
  activeMantra,
  onSessionComplete,
}: PitchVisualizerProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [pitchHz, setPitchHz] = useState<number | null>(null);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [score, setScore] = useState(100);
  const [matchCount, setMatchCount] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [isGuidePlaying, setIsGuidePlaying] = useState(false);
  const [hapticEnabled, setHapticEnabled] = useState(true);
  const [isMatchingNow, setIsMatchingNow] = useState(false);
  const [resonanceData, setResonanceData] = useState<SpectralCentroidResult | null>(null);
  const [micError, setMicError] = useState<string | null>(null);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastHapticTimeRef = useRef<number>(0);
  const analysisBufferRef = useRef<Float32Array>(new Float32Array(2048));

  // Auto-suspend AudioContext on tab concealment for battery efficiency
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (audioCtxRef.current && audioCtxRef.current.state === "running") {
          audioCtxRef.current.suspend();
        }
      } else {
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
          audioCtxRef.current.resume();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (e) {}
      }
    };
  }, []);

  // Guide oscillator reference
  const guideOscRef = useRef<OscillatorNode | null>(null);
  const guideGainRef = useRef<GainNode | null>(null);

  // Standard frequency references based on pitch profile
  const baseFreqs = {
    Udatta: 220, // A3
    Svarita: 165, // E3
    Anudatta: 110, // A2
    High: 220,
    Middle: 165,
    Low: 110
  };

  const currentSegment = activeMantra?.phoneticSegments?.[activeSegmentIndex] || activeMantra?.phoneticSegments?.[0] || {
    syllable: "Om",
    expectedPitch: "Svarita" as const,
    phoneticType: "Pranava",
    description: "Chant steadily"
  };

  const getExpectedFreq = (segment?: PhoneticSegment) => {
    if (!segment) return 165;
    return baseFreqs[segment.expectedPitch] || 165;
  };

  // Reset active segment index when active mantra changes
  useEffect(() => {
    setActiveSegmentIndex(0);
  }, [activeMantra?.id]);

  /**
   * Tactile Haptic Feedback Engine for Sanskrit Chanting
   * Triggers rhythmic vibration pulses when pitch aligns with target Vedic swara frequencies.
   */
  const triggerHapticFeedback = (pattern: number | number[] = [20, 30, 20]) => {
    if (!hapticEnabled) return;
    if (typeof window !== "undefined" && "vibrate" in navigator) {
      const now = Date.now();
      if (now - lastHapticTimeRef.current > 180) {
        try {
          navigator.vibrate(pattern);
          lastHapticTimeRef.current = now;
        } catch (e) {
          // Fallback if browser user-agent blocks unprompted vibration
        }
      }
    }
  };

  // Play a synthesized Web Audio reference guide tone with ADSR envelope
  const playGuideTone = () => {
    if (isGuidePlaying) {
      stopGuideTone();
      return;
    }

    try {
      const ctx = createAudioContext();
      if (!ctx) return;
      audioCtxRef.current = ctx;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      const expected = getExpectedFreq(currentSegment);
      osc.type = "sine";
      osc.frequency.setValueAtTime(expected, ctx.currentTime);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5);

      osc.connect(gain);
      gain.connect(ctx.destination);

      guideOscRef.current = osc;
      guideGainRef.current = gain;

      osc.start();
      osc.stop(ctx.currentTime + 1.5);

      setIsGuidePlaying(true);
      triggerHapticFeedback([15, 25]);
      setTimeout(() => {
        setIsGuidePlaying(false);
      }, 1500);

    } catch (e) {
      console.warn("Web Audio guide tone failed:", e);
    }
  };

  const stopGuideTone = () => {
    if (guideOscRef.current) {
      try { guideOscRef.current.stop(); } catch(e){}
      guideOscRef.current = null;
    }
    setIsGuidePlaying(false);
  };

  const startRecording = async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const ctx = createAudioContext();
      if (!ctx) return;
      audioCtxRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      source.connect(analyser);
      setIsRecording(true);
      setScore(100);
      setMatchCount(0);
      setTotalAttempts(0);
      setIsMatchingNow(false);
      triggerHapticFeedback([30, 40, 30]);

      detectPitchLoop();
    } catch (e) {
      console.warn("Microphone access denied or failed:", e);
      setMicError("Microphone permission is required for real-time Vedic pitch training. Please allow microphone access in your browser.");
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    setPitchHz(null);
    setIsMatchingNow(false);
    setResonanceData(null);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch(e){}
    }

    triggerHapticFeedback([40, 20]);

    if (onSessionComplete) {
      const matchPct = totalAttempts > 0 ? Math.round((matchCount / totalAttempts) * 100) : 0;
      onSessionComplete(score, matchPct, 30);
    }
  };

  const detectPitchLoop = () => {
    if (!analyserRef.current || !audioCtxRef.current) return;

    const bufferLength = analyserRef.current.fftSize;
    if (analysisBufferRef.current.length !== bufferLength) {
      analysisBufferRef.current = new Float32Array(bufferLength);
    }
    const buffer = analysisBufferRef.current;
    analyserRef.current.getFloatTimeDomainData(buffer);

    const sampleRate = audioCtxRef.current.sampleRate || 44100;
    const expected = getExpectedFreq(currentSegment);

    // Call decoupled DSP typed contract from @sovereign/vedic-phonetics
    const analysis: PitchDetectionResult = analyzeFrame(buffer, sampleRate, expected);

    if (analysis.resonance) {
      setResonanceData(analysis.resonance);
    }

    if (analysis.pitchHz > 0 && analysis.pitchHz > 70 && analysis.pitchHz < 600) {
      setPitchHz(analysis.pitchHz);

      const isMatch = Math.abs(analysis.pitchHz - expected) < 32 || analysis.swara === currentSegment.expectedPitch;

      setTotalAttempts(prev => prev + 1);
      if (isMatch) {
        setMatchCount(prev => prev + 1);
        setIsMatchingNow(true);
        triggerHapticFeedback([20, 30, 20]);

        if (Math.random() < 0.12 && activeSegmentIndex < activeMantra.phoneticSegments.length - 1) {
          setActiveSegmentIndex(prev => {
            triggerHapticFeedback([35, 45, 55]);
            return prev + 1;
          });
        }
      } else {
        setIsMatchingNow(false);
        setScore(prev => Math.max(40, prev - 0.2));
      }

      // Broadcast pitch-metric event for contour and karaoke visualizers
      if (typeof window !== "undefined") {
        const cents = 1200 * Math.log2(analysis.pitchHz / expected);
        window.dispatchEvent(
          new CustomEvent("pitch-metric", {
            detail: {
              pitch: analysis.pitchHz,
              accent: analysis.swara ? analysis.swara.toUpperCase() : "SVARITA",
              centDeviation: Math.round(cents),
              isMatch,
            },
          })
        );
      }
    } else {
      setIsMatchingNow(false);
    }

    drawOscilloscope(buffer);
    // Privacy-First Telemetry: Zero out PCM audio buffer from memory immediately after DSP processing
    buffer.fill(0);
    animationFrameRef.current = requestAnimationFrame(detectPitchLoop);
  };

  const autoCorrelate = (buffer: Float32Array, sampleRate: number): number => {
    // Trim silent signals
    let rms = 0;
    for (let i = 0; i < buffer.length; i++) {
      rms += buffer[i] * buffer[i];
    }
    rms = Math.sqrt(rms / buffer.length);
    if (rms < 0.01) return -1; // too silent

    // Find clipping
    let r1 = 0, r2 = buffer.length - 1;
    const thres = 0.2;
    for (let i = 0; i < buffer.length / 2; i++) {
      if (Math.abs(buffer[i]) < thres) { r1 = i; break; }
    }
    for (let i = buffer.length - 1; i >= buffer.length / 2; i--) {
      if (Math.abs(buffer[i]) < thres) { r2 = i; break; }
    }

    const trimmed = buffer.subarray(r1, r2);
    const correlations = new Float32Array(trimmed.length);
    for (let i = 0; i < trimmed.length; i++) {
      for (let j = 0; j < trimmed.length - i; j++) {
        correlations[i] += trimmed[j] * trimmed[j + i];
      }
    }

    // Find the first peak
    let d = 0;
    while (correlations[d] > correlations[d + 1]) d++;
    let maxval = -1;
    let maxpos = -1;
    for (let i = d; i < correlations.length; i++) {
      if (correlations[i] > maxval) {
        maxval = correlations[i];
        maxpos = i;
      }
    }

    let T0 = maxpos;
    return sampleRate / T0;
  };

  const drawOscilloscope = (buffer: Float32Array) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.fillStyle = "#020617";
    ctx.fillRect(0, 0, width, height);

    // Draw grid
    ctx.strokeStyle = "rgba(99, 102, 241, 0.05)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let x = 0; x < width; x += 40) {
      ctx.moveTo(x, 0); ctx.lineTo(x, height);
    }
    for (let y = 0; y < height; y += 30) {
      ctx.moveTo(0, y); ctx.lineTo(width, y);
    }
    ctx.stroke();

    // Draw waveform
    ctx.strokeStyle = isRecording ? "#06b6d4" : "rgba(99, 102, 241, 0.3)";
    ctx.lineWidth = 2;
    ctx.shadowBlur = 8;
    ctx.shadowColor = isRecording ? "#06b6d4" : "transparent";
    ctx.beginPath();

    const sliceWidth = width / buffer.length;
    let x = 0;

    for (let i = 0; i < buffer.length; i++) {
      const v = buffer[i] * 1.5;
      const y = (v + 1) * (height / 2);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      x += sliceWidth;
    }

    ctx.lineTo(width, height / 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
  };

  useEffect(() => {
    // Draw an initial flat line
    const flat = new Float32Array(512);
    drawOscilloscope(flat);
  }, []);

  return (
    <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-6 shadow-2xl relative overflow-hidden text-left">
      <div className="absolute top-0 right-0 h-40 w-40 bg-cyan-500/5 blur-3xl pointer-events-none rounded-full" />
      
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-900 pb-4 mb-6">
        <div>
          <span className="text-[10px] text-cyan-400 font-mono tracking-widest uppercase block mb-1">
            // LIVE OSCILLOSCOPE & PHONETICS
          </span>
          <h3 className="text-sm font-extrabold text-slate-100 uppercase tracking-wider font-display">
            Vocal Articulation & Pitch Alignment
          </h3>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              const nextState = !hapticEnabled;
              setHapticEnabled(nextState);
              if (nextState) triggerHapticFeedback([15, 25]);
            }}
            className={`px-3 py-1.5 rounded-xl border text-[10px] font-mono font-bold uppercase tracking-wider transition cursor-pointer flex items-center gap-1.5 ${
              hapticEnabled
                ? "bg-cyan-950/40 text-cyan-300 border-cyan-500/40 shadow-sm"
                : "bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-300"
            }`}
            title="Toggle Tactile Haptic Vibration Feedback on Pitch Match"
          >
            <Activity className="h-3.5 w-3.5 text-cyan-400" />
            <span>Tactile {hapticEnabled ? "ON" : "OFF"}</span>
          </button>

          <button
            onClick={playGuideTone}
            className={`px-3 py-1.5 rounded-xl border text-[10px] font-mono font-bold uppercase tracking-wider transition cursor-pointer flex items-center gap-1.5 ${
              isGuidePlaying
                ? "bg-amber-950/30 text-amber-300 border-amber-500/40"
                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-white"
            }`}
          >
            <Volume2 className="h-3.5 w-3.5" />
            <span>Guide Tone ({getExpectedFreq(currentSegment)} Hz)</span>
          </button>

          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`px-4 py-1.5 rounded-xl text-[10px] font-mono font-bold uppercase tracking-widest transition cursor-pointer flex items-center gap-1.5 ${
              isRecording
                ? "bg-rose-500/20 text-rose-300 border border-rose-500/30 animate-pulse"
                : "bg-cyan-600 hover:bg-cyan-500 text-slate-950 border border-cyan-400/20 shadow-md"
            }`}
          >
            {isRecording ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            <span>{isRecording ? "Stop & Analyze" : "Start Practice"}</span>
          </button>
        </div>
      </div>

      {micError && (
        <div className="mb-4 p-3.5 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center justify-between text-xs text-red-300">
          <span>{micError}</span>
          <button
            onClick={() => setMicError(null)}
            className="text-[10px] font-mono text-red-400 hover:text-red-200 underline ml-3"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* Oscilloscope Canvas (7 Columns) */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="relative rounded-2xl border border-slate-900 overflow-hidden bg-slate-950 aspect-video flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width="450"
              height="250"
              className="w-full h-full"
            />

            {/* Float Overlay Info */}
            <div className="absolute top-3 left-3 text-[9px] font-mono text-slate-500 uppercase bg-slate-950/90 border border-slate-900 px-2 py-0.5 rounded">
              DSP Target: {activeMantra.tongue} Resonator
            </div>

            {resonanceData && resonanceData.resonanceType !== 'Clear' && (
              <div className="absolute bottom-3 left-3 bg-amber-950/90 border border-amber-500/50 px-2.5 py-1 rounded-xl flex items-center gap-1.5 text-[9px] font-mono text-amber-300 shadow-lg animate-pulse">
                <Radio className="h-3 w-3 text-amber-400" />
                <span>
                  {resonanceData.resonanceType === 'Anusvara' ? 'Anusvara (ṁ) Nasal Resonance' : 'Visarga (ḥ) Aspiration'} ({resonanceData.centroidHz} Hz)
                </span>
              </div>
            )}

            {isMatchingNow && (
              <div className="absolute top-3 right-3 bg-emerald-950/90 border border-emerald-500/50 px-2.5 py-1 rounded-xl flex items-center gap-1.5 text-[9px] font-mono text-emerald-300 shadow-lg animate-pulse">
                <Sparkles className="h-3 w-3 text-emerald-400" />
                <span>SWARA MATCHED 🎯 (HAPTIC VIBRATION)</span>
              </div>
            )}

            {pitchHz && (
              <div className="absolute bottom-3 right-3 bg-slate-950/90 border border-cyan-500/30 px-3 py-1.5 rounded-xl text-center shadow-lg font-mono">
                <span className="text-[7px] text-slate-500 uppercase block tracking-wider">Detected Pitch</span>
                <span className="text-base font-extrabold text-cyan-400">{pitchHz} Hz</span>
              </div>
            )}
          </div>

          {/* Syllables Sequence Bar */}
          <div className="p-4 bg-slate-900/30 border border-slate-900 rounded-2xl space-y-2">
            <span className="text-[9px] font-mono text-slate-500 uppercase block font-bold tracking-widest">
              Syllable Chanting Progression Sequence
            </span>

            <div className="flex flex-wrap gap-1.5">
              {activeMantra.phoneticSegments.map((seg, idx) => {
                const isActive = activeSegmentIndex === idx;
                const isPassed = idx < activeSegmentIndex;
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      setActiveSegmentIndex(idx);
                      triggerHapticFeedback(15);
                    }}
                    className={`px-3 py-2 rounded-xl border text-center transition cursor-pointer flex flex-col items-center justify-center min-w-[56px] ${
                      isActive
                        ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                        : isPassed
                          ? "bg-slate-900/60 border-slate-800 text-slate-500"
                          : "bg-slate-950 border-slate-900 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <span className="text-xs font-extrabold font-display leading-none">{seg.syllable}</span>
                    <span className="text-[7.5px] font-mono uppercase tracking-widest mt-1 opacity-70">
                      {seg.expectedPitch}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Diagnostic Panel & Tips (5 Columns) */}
        <div className="lg:col-span-5 flex flex-col justify-between bg-slate-900/10 border border-slate-900 p-5 rounded-2xl relative">
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-900 pb-2.5">
              <Sparkles className="h-4.5 w-4.5 text-cyan-400" />
              <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider font-mono">
                Real-Time Auditory Feedback
              </h4>
            </div>

            {/* Scorecard */}
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-slate-950 border border-slate-900 p-3 rounded-xl font-mono">
                <span className="text-[7.5px] text-slate-500 uppercase block tracking-wider">Acoustic Accuracy</span>
                <span className="text-xl font-black text-cyan-400 mt-1 block">{Math.round(score)}%</span>
              </div>
              <div className="bg-slate-950 border border-slate-900 p-3 rounded-xl font-mono">
                <span className="text-[7.5px] text-slate-500 uppercase block tracking-wider">Accent Matches</span>
                <span className="text-xl font-black text-amber-500 mt-1 block">
                  {totalAttempts > 0 ? Math.round((matchCount / totalAttempts) * 100) : 0}%
                </span>
              </div>
            </div>

            {/* Active Segment Detail */}
            <div className="p-3 bg-slate-950/60 border border-slate-900 rounded-xl space-y-1">
              <span className="text-[7.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">// Active Syllable Detail:</span>
              <div className="flex justify-between items-baseline">
                <strong className="text-sm font-display text-slate-200">{currentSegment.syllable}</strong>
                <span className="text-[10px] font-mono text-cyan-400">{currentSegment.phoneticType}</span>
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed font-light">
                {currentSegment.description}
              </p>
            </div>

            {/* Pronunciation Tips list */}
            <div className="space-y-1.5">
              <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block font-bold">Pronunciation Guidelines:</span>
              <ul className="space-y-1 text-[10.5px] text-slate-300 font-light leading-normal pl-1.5 list-disc list-inside">
                {(activeMantra?.pronunciationTips || []).map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="p-3 mt-4 bg-slate-950/60 rounded-xl border border-slate-900/60 text-[9px] font-mono text-slate-500 leading-relaxed flex items-start gap-2">
            <Info className="h-3.5 w-3.5 text-cyan-500 shrink-0 mt-0.5" />
            <span>Maintain comfortable, diaphragmatic breathing. Adjust your vocal position relative to the mic to stabilize signals.</span>
          </div>
        </div>

      </div>
    </div>
  );
}
